import { AppShell, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Outlet, useLocation, Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Me } from '@nelnela/contracts';
import { TtsBar } from '../components/TtsBar';
import { InfoStrip } from '../components/InfoStrip';
import { Mark } from '../components/brand/Mark';
import {
  IconMe,
  IconPractice,
  IconToday,
  IconWatch,
  IconWords,
} from '../components/brand/nav-icons';
import { useSw } from '../lib/sw';
import { RAIL_WIDTH, TAB_BAR_HEIGHT, UPDATE_BAR_HEIGHT } from './layout';

interface NavItem {
  to: string;
  label: string;
  glyph: (active: boolean, size: number) => ReactNode;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Today', glyph: (active, size) => <IconToday size={size} active={active} /> },
  { to: '/watch', label: 'Watch', glyph: (_a, size) => <IconWatch size={size} /> },
  { to: '/practice', label: 'Practice', glyph: (_a, size) => <IconPractice size={size} /> },
  { to: '/words', label: 'Words', glyph: (_a, size) => <IconWords lineHeight={size} /> },
  { to: '/me', label: 'Me', glyph: (_a, size) => <IconMe size={size} /> },
];

/**
 * Which tab owns a path. Exact for Today (every route is under `/`), prefix for the rest — but
 * a prefix that has to end at a segment boundary, so a future `/watchlist` could never light up
 * Watch.
 */
export function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * The reading surface takes the whole screen: the tab bar and the update line both stand down.
 *
 * Watch since M5 (the player is the screen), and M6a's story reader for the same reason — frame
 * `5a` is a full-bleed page of Georgian with a scroll-progress rule at its top edge, and a tab
 * bar under it would put five glyphs between the reader and the last line of the story. The
 * LIST at `/read` is not immersive: it is a menu, and a menu needs its way out.
 *
 * M6c adds the journal editor and the chat run for a harder reason than composition: both dock
 * controls at the bottom edge of the viewport against a soft keyboard — the journal a correction
 * card AND a suggestion strip, the chat three reply chips AND a composer — and a 58px tab bar
 * underneath them reproduces M6a's standing defect 4 ("on a phone the Check / Next controls can
 * sit under the keyboard") on two screens that are entirely keyboard-driven. The lists at
 * `/journal` and `/chat` stay non-immersive by the same rule as `/read`: they are menus.
 */
export function isImmersive(pathname: string): boolean {
  return /^\/(watch|read|journal|chat)\/.+/.test(pathname);
}

/**
 * A FLOW: a route that replaces the app rather than sitting inside it — no rail, no tab bar, no
 * update strip, no now-playing bar.
 *
 * Stronger than `isImmersive`, and needed because immersive only hides the MOBILE tab bar; the
 * desktop rail stays. M10-α's call on the placement wizard is explicit: "desktop is the same
 * single column, centered — no rail (the wizard is a full-screen flow, chrome would invite
 * wandering mid-run)". A learner who wanders out of a placement run mid-band loses that band, so
 * the chrome that invites it is the thing to remove.
 *
 * A flow still renders inside `Authed`, so it keeps the session and `useStudy` — what it drops is
 * the furniture.
 */
export function isFlow(pathname: string): boolean {
  return pathname === '/placement';
}

const ACTIVE_TINT = 'color-mix(in srgb, var(--nn-accent) 13%, transparent)';

/**
 * App chrome per Today `2a` (desktop icon rail) and `2b` (mobile tab bar). Each screen owns its
 * contextual header; there is no global top bar (design handoff 11d).
 *
 * The rail lost 136px in the design pass — 200px of labelled NavLinks became a 64px column of
 * 18px icons under 9px labels. Labels stayed: the rail is icon *and* text, never icon-only and
 * never tooltips. The name·plan footer went; it lives on the Me account card, which is where
 * someone looks for it.
 */
export function Shell({ me }: { me: Me }) {
  const desktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });
  const { pathname } = useLocation();
  const { needRefresh, updateSW } = useSw();
  const immersive = isImmersive(pathname);
  const flow = isFlow(pathname);

  // Never mid-Watch: a reload there loses the study session, and the event log is the product.
  const showUpdate = needRefresh && !immersive;
  const tabBarShows = !desktop && !immersive;
  const footerHeight = tabBarShows
    ? TAB_BAR_HEIGHT + (showUpdate ? UPDATE_BAR_HEIGHT : 0)
    : undefined;

  const updateStrip = (
    <InfoStrip
      border="top"
      action={
        <UnstyledButton
          onClick={() => void updateSW?.(true)}
          style={{ fontSize: 11.5, color: 'var(--nn-accent-text)', flexShrink: 0 }}
        >
          Reload
        </UnstyledButton>
      }
    >
      A new version is ready
    </InfoStrip>
  );

  // A flow takes the whole screen: the outlet, and nothing else this component would add.
  if (flow) return <Outlet context={me} />;

  return (
    <AppShell
      navbar={desktop ? { width: RAIL_WIDTH, breakpoint: 'md' } : undefined}
      footer={footerHeight ? { height: footerHeight } : undefined}
      padding={0}
    >
      {desktop && (
        <AppShell.Navbar
          // Mantine resolves its style props AFTER `style`, so `padding` here and `p={0}` there
          // is not a belt-and-braces pair — the prop silently wins and the rail loses its
          // vertical padding. Say it once, as the prop.
          py={14}
          px={0}
          style={{
            width: RAIL_WIDTH,
            alignItems: 'center',
            gap: 6,
            borderRight: '1px solid var(--nn-hairline)',
          }}
        >
          <Mark size={24} style={{ marginBottom: 10 }} />
          {NAV.map((n) => (
            <RailItem key={n.to} item={n} active={isActive(pathname, n.to)} />
          ))}
        </AppShell.Navbar>
      )}

      <AppShell.Main>
        <Outlet context={me} />
      </AppShell.Main>

      {/* Desktop has no footer, so the update line pins to the bottom of the main area. */}
      {desktop && showUpdate && (
        <div style={{ position: 'fixed', left: RAIL_WIDTH, right: 0, bottom: 0, zIndex: 195 }}>
          {updateStrip}
        </div>
      )}

      {/* TTS now-playing bar (M3): fixed at the bottom on every route — above the mobile tab bar
          and above the update line when either shows, above the safe area on Watch (never over
          the player, which is at the top); centred within the desktop main area. */}
      <TtsBar
        bottomOffset={tabBarShows ? footerHeight! : desktop && showUpdate ? UPDATE_BAR_HEIGHT : 0}
        leftOffset={desktop ? RAIL_WIDTH : 0}
      />

      {tabBarShows && (
        <AppShell.Footer
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderTop: '1px solid var(--nn-hairline)',
            // The light canvas draws a white tab bar; dark draws it flush with the app bg.
            background: 'light-dark(var(--nn-panel), var(--nn-bg))',
          }}
        >
          {showUpdate && updateStrip}
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            {NAV.map((n) => (
              <TabItem key={n.to} item={n} active={isActive(pathname, n.to)} />
            ))}
          </div>
        </AppShell.Footer>
      )}
    </AppShell>
  );
}

/** Desktop rail pill: 48px wide, an 18px icon over a visible 9px label. */
function RailItem({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <UnstyledButton
      component={Link}
      to={item.to}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: 48,
        padding: '6px 0',
        borderRadius: 10,
        background: active ? ACTIVE_TINT : undefined,
        color: active ? 'var(--nn-accent)' : 'var(--nn-faint)',
        lineHeight: 1,
      }}
    >
      {item.glyph(active, 18)}
      <span style={{ fontSize: 9 }}>{item.label}</span>
    </UnstyledButton>
  );
}

/** Mobile tab: one of five equal targets, a 19px glyph over a 10px label. */
function TabItem({ item, active }: { item: NavItem; active: boolean }) {
  return (
    <UnstyledButton
      component={Link}
      to={item.to}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        color: active ? 'var(--nn-accent)' : 'var(--nn-faint)',
        lineHeight: 1,
      }}
    >
      {item.glyph(active, 19)}
      <span style={{ fontSize: 10 }}>{item.label}</span>
    </UnstyledButton>
  );
}
